import React, { useState } from "react";
import { useGetCommonMisconception } from "@packages/api/madlenAPI";
import {
  Expand,
  AlertCircle,
  CheckCircle,
  Brain,
  Lightbulb,
  GraduationCap,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import Text from "@core/text";
import { Button } from "@core/index";
import MisconceptionCard from "@features/tools/components/misconceptions/MisconceptionCard";
import SpinnerLoader from "../components/SpinnerLoader";

interface CommonMisconceptionProps {
  id: number | null;
}

const MisconceptionDetailComponent = ({
  tool,
  closeModal,
}: {
  tool: any;
  closeModal: () => void;
}) => {
  return (
    <div
      className="fixed inset-0 z-[9999] overflow-hidden bg-black bg-opacity-75 dark:bg-opacity-90 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closeModal();
        }
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <Text variant={"h1"}>{tool.title || "Common Misconceptions"}</Text>
          <Button
            onClick={closeModal}
            variant="solid"
            size="icon"
            rounded="full"
            bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X size={24} className="text-gray-500 dark:text-gray-400" />
          </Button>
        </div>

        <div className="flex-1 overflow-auto p-6 modal-content">
          <MisconceptionCard misconception={tool} />
        </div>
      </div>
    </div>
  );
};

const CommonMisconception: React.FC<CommonMisconceptionProps> = ({ id }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading, isError } = useGetCommonMisconception(id, {
    query: {
      refetchInterval: (data) => {
        if (
          data?.state.data?.misconceptions &&
          data.state.data.misconceptions?.length > 0
        ) {
          return false;
        }
        return 2000;
      },
      enabled: !!id,
      staleTime: Infinity,
      cacheTime: 1000 * 60 * 30,
    },
  });

  const isContentLoading =
    isLoading || !data?.misconceptions || data.misconceptions.length === 0;

  const handleExpandClick = () => {
    setExpanded(true);
  };

  const closeModal = () => {
    setExpanded(false);
  };

  if (isContentLoading) {
    return <SpinnerLoader />;
  }

  if (isError) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm mb-6">
        <h3 className="text-xl font-medium mb-4 text-red-500 dark:text-red-400">
          Unable to load Common Misconceptions
        </h3>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
          Please try regenerating the lesson
        </p>
      </div>
    );
  }

  return (
    <>
      {expanded && data && (
        <MisconceptionDetailComponent
          tool={{
            ...data,
            gradeLevel: data.gradeLevel === -1 ? 0 : data.gradeLevel,
          }}
          closeModal={closeModal}
        />
      )}

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm mb-6">
        <div className="flex w-full justify-between items-center">
          <h3 className="text-xl font-medium mb-4 flex items-center justify-between text-gray-900 dark:text-gray-100">
            <div>{t("common.title")}</div>
          </h3>
          <button
            className="flex items-center justify-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition duration-200"
            onClick={handleExpandClick}
          >
            <Expand size={18} className="text-gray-400 dark:text-gray-500" />
          </button>
        </div>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
          {t("common.subtitle")}
        </p>

        {/* Assessment Strategies */}
        {data.assessmentStrategies && (
          <div className="space-y-6 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-xl bg-teal-100 dark:bg-teal-900/50 text-teal-600 dark:text-teal-400">
                <CheckCircle size={20} />
              </div>
              <h4 className="font-semibold text-teal-600 dark:text-teal-400">
                {t(
                  "misconceptions.assessmentStrategies",
                  "Assessment Strategies",
                )}
              </h4>
            </div>

            {data.assessmentStrategies.detection_methods &&
              data.assessmentStrategies.detection_methods.length > 0 && (
                <div>
                  <h5 className="font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t("misconceptions.detectionMethods", "Detection Methods")}
                  </h5>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
                    {data.assessmentStrategies.detection_methods.map(
                      (method, idx) => (
                        <li key={idx}>
                          <Markdown>{method}</Markdown>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              )}

            {data.assessmentStrategies.re_evaluation_methods &&
              data.assessmentStrategies.re_evaluation_methods.length > 0 && (
                <div>
                  <h5 className="font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t(
                      "misconceptions.reEvaluationMethods",
                      "Re-evaluation Methods",
                    )}
                  </h5>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
                    {data.assessmentStrategies.re_evaluation_methods.map(
                      (method, idx) => (
                        <li key={idx}>
                          <Markdown>{method}</Markdown>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              )}
          </div>
        )}

        <div className="space-y-8">
          {data.misconceptions &&
            Array.isArray(data.misconceptions) &&
            data.misconceptions.slice(0, 1).map((item, idx) => (
              <div key={idx}>
                <div className="space-y-8">
                  {item.misconception && (
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 rounded-xl bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400">
                          <AlertCircle size={20} />
                        </div>
                        <h4 className="font-semibold text-red-600 dark:text-red-400">
                          {t("misconceptions.commonMisconception")}
                        </h4>
                      </div>
                      <Text
                        variant="p"
                        className="leading-relaxed text-gray-800 dark:text-gray-200"
                      >
                        <Markdown>{item.misconception}</Markdown>
                      </Text>
                    </div>
                  )}

                  {item.clarification && (
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 rounded-xl bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400">
                          <CheckCircle size={20} />
                        </div>
                        <h4 className="font-semibold text-green-600 dark:text-green-400">
                          {t("misconceptions.clarification")}
                        </h4>
                      </div>
                      <Text
                        variant="p"
                        className="leading-relaxed text-gray-800 dark:text-gray-200"
                      >
                        <Markdown>{item.clarification}</Markdown>
                      </Text>
                    </div>
                  )}

                  {item.why_misconception_occurs && (
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 rounded-xl bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400">
                          <Brain size={20} />
                        </div>
                        <h4 className="font-semibold text-blue-600 dark:text-blue-400">
                          {t("misconceptions.whyOccurs")}
                        </h4>
                      </div>
                      <Text
                        variant="p"
                        className="leading-relaxed text-gray-800 dark:text-gray-200"
                      >
                        <Markdown>{item.why_misconception_occurs}</Markdown>
                      </Text>
                    </div>
                  )}

                  {item.fix_strategies && (
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 rounded-xl bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400">
                          <Lightbulb size={20} />
                        </div>
                        <h4 className="font-semibold text-purple-600 dark:text-purple-400">
                          {t("misconceptions.fixStrategies")}
                        </h4>
                      </div>
                      <Text
                        variant="p"
                        className="leading-relaxed text-gray-800 dark:text-gray-200"
                      >
                        <Markdown>{item.fix_strategies}</Markdown>
                      </Text>
                    </div>
                  )}

                  {item.real_classroom_example && (
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 rounded-xl bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400">
                          <GraduationCap size={20} />
                        </div>
                        <h4 className="font-semibold text-amber-600 dark:text-amber-400">
                          {t("misconceptions.connectionsExample")}
                        </h4>
                      </div>
                      <Text
                        variant="p"
                        className="leading-relaxed text-gray-800 dark:text-gray-200"
                      >
                        <Markdown>{item.real_classroom_example}</Markdown>
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>
    </>
  );
};

export default CommonMisconception;
